import React from 'react';
import { CheckCircle, Heart, Users, Target, Lightbulb } from 'lucide-react';
import { SEO } from '../components/layout/SEO';
import { Section } from '../components/ui/Section';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { FadeIn } from '../components/ui/FadeIn';
import { useLanguage } from '../context/LanguageContext';
export function AboutPage() {
  const {
    t
  } = useLanguage();
  const values = [{
    icon: Heart,
    title: t('about.values.compassion.title'),
    description: t('about.values.compassion.description')
  }, {
    icon: CheckCircle,
    title: t('about.values.integrity.title'),
    description: t('about.values.integrity.description')
  }, {
    icon: Users,
    title: t('about.values.unity.title'),
    description: t('about.values.unity.description')
  }, {
    icon: Target,
    title: t('about.values.excellence.title'),
    description: t('about.values.excellence.description')
  }];
  const purposes = [{
    title: t('about.purpose.networking.title'),
    description: t('about.purpose.networking.description')
  }, {
    title: t('about.purpose.alumni.title'),
    description: t('about.purpose.alumni.description')
  }, {
    title: t('about.purpose.relief.title'),
    description: t('about.purpose.relief.description')
  }, {
    title: t('about.purpose.education.title'),
    description: t('about.purpose.education.description')
  }, {
    title: t('about.purpose.community.title'),
    description: t('about.purpose.community.description')
  }, {
    title: t('about.purpose.ethics.title'),
    description: t('about.purpose.ethics.description')
  }];
  return <>
      <SEO title={t('nav.about')} description={t('about.hero.subtitle')} data-id="element-141" />

      {/* Hero */}
      <div className="bg-gradient-to-br from-primary-900 via-primary-800 to-primary-900 text-white py-20 md:py-28" data-id="element-142">
        <div className="container mx-auto px-4 md:px-6" data-id="element-143">
          <FadeIn className="max-w-3xl mx-auto text-center" data-id="element-144">
            <img src="https://cdn.magicpatterns.com/uploads/stkewdFoDLsveAsLMC6296/favi__logo.png" alt="KMA UK Logo" className="h-24 w-24 mx-auto mb-6 object-contain brightness-0 invert opacity-90" data-id="element-145" />
            <h1 className="text-4xl md:text-5xl font-serif font-bold mb-6" data-id="element-146">
              {t('about.hero.title')}
            </h1>
            <p className="text-xl text-primary-50 leading-relaxed" data-id="element-147">
              {t('about.hero.subtitle')}
            </p>
          </FadeIn>
        </div>
      </div>

      {/* Who We Are */}
      <Section data-id="element-148">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-149">
          <FadeIn data-id="element-150">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-primary-900 mb-6" data-id="element-151">
              {t('about.whoWeAre.title')}
            </h2>
            <div className="prose prose-lg max-w-none text-gray-600 leading-relaxed space-y-4" data-id="element-152">
              <p data-id="element-153">{t('about.whoWeAre.p1')}</p>
              <p data-id="element-154">{t('about.whoWeAre.p2')}</p>
              <p data-id="element-155">{t('about.whoWeAre.p3')}</p>
            </div>
          </FadeIn>
          <FadeIn delay={0.2} data-id="element-156">
            <div className="relative" data-id="element-157">
              <div className="absolute -inset-4 bg-primary-50 rounded-2xl transform rotate-3" data-id="element-158" />
              <img src="https://images.unsplash.com/photo-1511795409834-ef04bbd61622?ixlib=rb-4.0.3&auto=format&fit=crop&w=2091&q=80" alt="Asian medical professionals at networking event" className="relative rounded-xl shadow-xl w-full" data-id="element-159" />
            </div>
          </FadeIn>
        </div>
      </Section>

      {/* Important Statement */}
      <Section background="accent" data-id="element-160">
        <FadeIn data-id="element-161">
          <div className="max-w-4xl mx-auto" data-id="element-162">
            <div className="bg-white border-l-4 border-accent-700 p-8 rounded-r-xl shadow-md" data-id="element-163">
              <div className="flex items-start gap-4" data-id="element-164">
                <div className="flex-shrink-0" data-id="element-165">
                  <div className="h-12 w-12 rounded-full bg-accent-100 flex items-center justify-center" data-id="element-166">
                    <CheckCircle className="h-6 w-6 text-accent-700" data-id="element-167" />
                  </div>
                </div>
                <div data-id="element-168">
                  <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-169">
                    {t('about.independence.title')}
                  </h3>
                  <p className="text-gray-700 text-lg leading-relaxed" data-id="element-170">
                    {t('about.independence.description')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </FadeIn>
      </Section>

      {/* Our Purpose */}
      <Section data-id="element-171">
        <div className="max-w-4xl mx-auto" data-id="element-172">
          <FadeIn className="text-center mb-12" data-id="element-173">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-primary-900 mb-6" data-id="element-174">
              {t('about.purpose.title')}
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed" data-id="element-175">
              {t('about.purpose.subtitle')}
            </p>
          </FadeIn>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-176">
            {purposes.map((item, i) => <FadeIn key={i} delay={i * 0.05} data-id="element-177">
                <Card className="h-full border-primary-100 hover:shadow-md transition-shadow" data-id="element-178">
                  <CardContent className="p-6" data-id="element-179">
                    <h3 className="text-lg font-bold text-primary-900 mb-2" data-id="element-180">
                      {item.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed" data-id="element-181">
                      {item.description}
                    </p>
                  </CardContent>
                </Card>
              </FadeIn>)}
          </div>
        </div>
      </Section>

      {/* Our Values */}
      <Section background="gray" data-id="element-182">
        <div className="text-center max-w-3xl mx-auto mb-12" data-id="element-183">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-primary-900 mb-4" data-id="element-184">
            {t('about.values.title')}
          </h2>
          <p className="text-gray-600" data-id="element-185">{t('about.values.subtitle')}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-186">
          {values.map((value, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-187">
              <Card className="h-full text-center border-none shadow-lg hover:shadow-xl transition-shadow" data-id="element-188">
                <CardHeader className="pb-4" data-id="element-189">
                  <div className="h-16 w-16 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center mb-4 mx-auto" data-id="element-190">
                    <value.icon className="h-8 w-8" data-id="element-191" />
                  </div>
                  <CardTitle className="text-xl" data-id="element-192">{value.title}</CardTitle>
                </CardHeader>
                <CardContent data-id="element-193">
                  <p className="text-gray-600 text-sm leading-relaxed" data-id="element-194">
                    {value.description}
                  </p>
                </CardContent>
              </Card>
            </FadeIn>)}
        </div>
      </Section>

      {/* Our Future Vision */}
      <Section data-id="element-195">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-196">
          <FadeIn data-id="element-197">
            <img src="https://images.unsplash.com/photo-1551076805-e1869033e561?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80" alt="Healthcare professionals planning" className="rounded-xl shadow-xl w-full" data-id="element-198" />
          </FadeIn>
          <FadeIn delay={0.2} data-id="element-199">
            <div className="flex items-start gap-4 mb-6" data-id="element-200">
              <div className="flex-shrink-0" data-id="element-201">
                <div className="h-12 w-12 rounded-full bg-accent-100 flex items-center justify-center" data-id="element-202">
                  <Lightbulb className="h-6 w-6 text-accent-700" data-id="element-203" />
                </div>
              </div>
              <div data-id="element-204">
                <h2 className="text-3xl md:text-4xl font-serif font-bold text-primary-900 mb-4" data-id="element-205">
                  {t('about.vision.title')}
                </h2>
              </div>
            </div>
            <div className="prose prose-lg max-w-none text-gray-600 leading-relaxed space-y-4" data-id="element-206">
              <p data-id="element-207">{t('about.vision.p1')}</p>
              <p data-id="element-208">{t('about.vision.p2')}</p>
              <p data-id="element-209">{t('about.vision.p3')}</p>
              <p className="font-medium text-primary-800" data-id="element-210">
                {t('about.vision.p4')}
              </p>
            </div>
          </FadeIn>
        </div>
      </Section>

      {/* History Timeline */}
      <Section background="gray" data-id="element-211">
        <div className="max-w-3xl mx-auto" data-id="element-212">
          <div className="text-center mb-12" data-id="element-213">
            <h2 className="text-3xl font-serif font-bold text-primary-900 mb-4" data-id="element-214">
              {t('about.journey.title')}
            </h2>
            <p className="text-gray-600" data-id="element-215">{t('about.journey.subtitle')}</p>
          </div>

          <div className="space-y-12 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-primary-300 before:to-transparent" data-id="element-216">
            {[{
            year: '1985',
            title: 'Foundation',
            desc: 'KMA UK was established by a group of visionary doctors committed to professional excellence.'
          }, {
            year: '1995',
            title: 'Charitable Status',
            desc: 'Officially registered as a charity in England & Wales, expanding our humanitarian mission.'
          }, {
            year: '2005',
            title: 'Earthquake Relief',
            desc: 'Major humanitarian mission deployed to Northern Pakistan following devastating earthquake.'
          }, {
            year: '2010',
            title: 'Education Fund',
            desc: 'Launched scholarship program for deserving medical students from KPK.'
          }, {
            year: '2022',
            title: 'Flood Response',
            desc: 'Coordinated emergency medical relief for flood-affected regions across Pakistan.'
          }, {
            year: '2024',
            title: 'Digital Era',
            desc: 'Launching digital initiatives to connect the next generation of medical professionals.'
          }].map((item, i) => <FadeIn key={i} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group" data-id="element-217">
                <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-white bg-primary-600 shadow-lg shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10" data-id="element-218">
                  <div className="w-3 h-3 bg-white rounded-full" data-id="element-219" />
                </div>
                <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] p-6 bg-white rounded-lg shadow-md border border-gray-100 hover:shadow-lg transition-shadow" data-id="element-220">
                  <div className="flex items-center justify-between space-x-2 mb-2" data-id="element-221">
                    <h3 className="font-bold text-gray-900 text-lg" data-id="element-222">
                      {item.title}
                    </h3>
                    <time className="font-serif font-bold text-accent-600 text-lg" data-id="element-223">
                      {item.year}
                    </time>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed" data-id="element-224">
                    {item.desc}
                  </p>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </Section>
    </>;
}